import React, { useEffect, useRef } from 'react';

interface SideBySideEditorProps {
  url1: string;
  url2: string;
  onSave?: (url1: string, url2: string) => void;
  onCancel?: () => void;
}

export function SideBySideEditor({ url1, url2, onSave, onCancel }: SideBySideEditorProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Check if we're in single image mode (both URLs are the same)
  const isSingleMode = url1 === url2;

  // Construct editor URL based on mode
  const editorUrl = isSingleMode
    ? `https://cloudinary-canvas-studio.lovable.app/?url=${encodeURIComponent(url2)}&embed=true`
    : `https://cloudinary-canvas-studio.lovable.app/?url1=${encodeURIComponent(url1)}&url2=${encodeURIComponent(url2)}&embed=true`;

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Debug logging to help diagnose postMessage issues
      console.log('[SideBySideEditor] Received postMessage:', {
        type: event.data.type,
        origin: event.origin,
        data: event.data,
        isSingleMode
      });

      // Handle both SAVE_IMAGE (singular, from single mode) and SAVE_IMAGES (plural, from side-by-side mode)
      if (event.data.type === 'SAVE_IMAGES' || event.data.type === 'SAVE_IMAGE') {
        if (isSingleMode) {
          // In single mode, both urls should be the same (the edited image)
          // Try multiple possible property names from the iframe app
          const editedUrl = event.data.url || event.data.croppedUrl || event.data.url1 || event.data.url2;

          if (editedUrl) {
            console.log('[SideBySideEditor] Saving single image:', editedUrl);
            onSave?.(editedUrl, editedUrl);
          } else {
            console.error('[SideBySideEditor] No URL found in save message:', event.data);
          }
        } else {
          // Side-by-side mode
          const url1 = event.data.url1;
          const url2 = event.data.url2;

          if (url1 && url2) {
            console.log('[SideBySideEditor] Saving side-by-side images:', { url1, url2 });
            onSave?.(url1, url2);
          } else {
            console.error('[SideBySideEditor] Missing URLs in save message:', event.data);
          }
        }
      } else if (event.data.type === 'CANCEL') {
        console.log('[SideBySideEditor] Cancel requested');
        onCancel?.();
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [onSave, onCancel, isSingleMode]);

  // Cleanup iframe on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      // Clear iframe src to help browser garbage collect
      if (iframeRef.current) {
        iframeRef.current.src = 'about:blank';
      }
    };
  }, []);

  return (
    <iframe
      ref={iframeRef}
      src={editorUrl}
      width="100%"
      height="100%"
      style={{ border: 'none' }}
      allow="clipboard-write"
      title={isSingleMode ? "Image Editor" : "Side-by-Side Image Editor"}
    />
  );
}
